import { useCallback, useEffect, useState } from 'react'
import { Accordion, AccordionItem } from '@overdose/components'
import { IconX } from '@tabler/icons-react'
import classNames from 'classnames'
import { Button } from '../Button'
import { Checkbox } from '../Checkbox'
import { Drawer } from '../Drawer'
import { Pill } from '../Pill'
import { Radio } from '../Radio'
import Typography, { TypographyTag, TypographyVariant } from '../Typography'
import styles from './FiltersDrawer.module.css'
import { FiltersDrawerProps, FilterMapProps } from './FiltersDrawer.types'
import { PriceInput } from './PriceInput'
import { SearchInput } from './SearchInput'

const FiltersDrawer = ({
  open = false,
  filters,
  selected,
  onChange,
  onClose,
}: FiltersDrawerProps) => {
  const [selectedFilterMap, setSelectedFilterMap] = useState<FilterMapProps>({})

  useEffect(() => {
    if (open) {
      setSelectedFilterMap(selected)
    }
  }, [open, selected, setSelectedFilterMap])

  const handleChangeFilter = useCallback(
    (uid: string, checked: boolean, value: string) => {
      let updatedSelectedFilterMap = {}
      const currentOptions = selectedFilterMap[uid] || []

      if (checked) {
        updatedSelectedFilterMap = {
          ...selectedFilterMap,
          [uid]: currentOptions.concat(value),
        }
      } else {
        updatedSelectedFilterMap = {
          ...selectedFilterMap,
          [uid]: currentOptions.filter((el) => {
            return el.toLowerCase() !== value.toLowerCase()
          }),
        }
      }
      setSelectedFilterMap(updatedSelectedFilterMap)
    },
    [selectedFilterMap, setSelectedFilterMap]
  )

  const handleChangePriceFilter = useCallback(
    (uid: string, opIndex: number, value: string) => {
      const currentFilterMap = selectedFilterMap[uid] || []
      const updatedSelectedFilterMap = {
        ...selectedFilterMap,
        [uid]: [...currentFilterMap],
      }
      updatedSelectedFilterMap[uid][opIndex] = value
      setSelectedFilterMap(updatedSelectedFilterMap)
    },
    [selectedFilterMap, setSelectedFilterMap]
  )

  const handleChangeSearchFilter = useCallback(
    (uid: string, value: string) => {
      const currentFilterMap = selectedFilterMap[uid] || []
      const updatedSelectedFilterMap = {
        ...selectedFilterMap,
        [uid]: [...currentFilterMap],
      }
      updatedSelectedFilterMap[uid][0] = value
      setSelectedFilterMap(updatedSelectedFilterMap)
    },
    [selectedFilterMap, setSelectedFilterMap]
  )

  const handleClose = () => {
    onClose?.()
  }

  const handleApplyFilters = useCallback(() => {
    onChange?.(selectedFilterMap)
  }, [onChange, selectedFilterMap])

  const handleClearFilters = useCallback(() => {
    const currentFilter = {
      ...(selected.q && { q: selected.q }),
    }
    onChange?.(currentFilter)
  }, [onChange])

  const drawerTitle = (
    <Typography
      variant={TypographyVariant.BodyXLarge}
      tag={TypographyTag.span}
      className='!font-bold text-primary-heading'>
      Filters
    </Typography>
  )

  const renderLabel = useCallback((text: string, count: number) => {
    return (
      <div className='flex mr-4'>
        <div className='flex-1'>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegular}>
            {text}
          </Typography>
        </div>
        <div>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegular}>
            {count}
          </Typography>
        </div>
      </div>
    )
  }, [])

  const renderSelected = useCallback(() => {
    const selectedList = filters?.reduce((reducer, ft) => {
      const filteredOptions = ft.options
        .filter((op) => {
          return selectedFilterMap?.[ft.uid]?.includes(op.uid)
        })
        .map((op) => {
          return { ...op, fuid: ft.uid }
        })
      return [...reducer, ...filteredOptions]
    }, [])

    if (!selectedList?.length) {
      return null
    }

    return (
      <div className='px-4 lg:px-6 py-5 border-b'>
        <Typography
          variant={TypographyVariant.BodyLargeBold}
          tag={TypographyTag.span}>
          Applied Filters
        </Typography>
        <div className='flex items-center flex-wrap gap-2 mt-4'>
          {selectedList.map((sel, index) => {
            return (
              <Pill
                key={index}
                title={sel.text}
                icon={<IconX size={16} />}
                iconAlignments='left'
                onCheck={() => {
                  return handleChangeFilter(sel.fuid, false, sel.uid)
                }}
              />
            )
          })}
        </div>
      </div>
    )
  }, [filters, selectedFilterMap, handleChangeFilter])

  const renderContent = () => {
    return (
      <div className='flex-1 w-full px-4 lg:px-6 overflow-y-auto'>
        <Accordion>
          {filters?.map((filter, fidx) => {
            return (
              <AccordionItem
                key={fidx}
                name={filter.uid}
                theme={{
                  control: styles.itemHead,
                  content_inner: styles.itemContent,
                }}
                header={filter.text}>
                {filter.selectionType === 'q' && (
                  <div className={classNames('py-2')}>
                    <SearchInput
                      name='q'
                      defaultValue={filter.options?.[0]?.value || ''}
                      value={selectedFilterMap?.q?.[0]}
                      onChange={(value: string) => {
                        return handleChangeSearchFilter('q', value)
                      }}
                    />
                  </div>
                )}
                {filter.selectionType === 'pricerange' && (
                  <>
                    <div className={classNames(styles.filterOption, 'py-2')}>
                      <div className='flex-1'>
                        <Typography
                          tag={TypographyTag.p}
                          variant={TypographyVariant.BodyRegular}>
                          Min Price
                        </Typography>
                      </div>
                      <div>
                        <PriceInput
                          name='minPrice'
                          defaultValue={filter.options?.[0]?.value || '0'}
                          value={selectedFilterMap?.minPrice?.[0]}
                          onChange={(value: string) => {
                            return handleChangePriceFilter('minPrice', 0, value)
                          }}
                        />
                      </div>
                    </div>
                    <div className={styles.filterOption}>
                      <div className='flex-1'>
                        <Typography
                          tag={TypographyTag.p}
                          variant={TypographyVariant.BodyRegular}>
                          Max Price
                        </Typography>
                      </div>
                      <div>
                        <PriceInput
                          name='maxPrice'
                          defaultValue={filter.options?.[1]?.value || '0'}
                          value={selectedFilterMap?.maxPrice?.[0]}
                          onChange={(value: string) => {
                            return handleChangePriceFilter('maxPrice', 0, value)
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
                {filter.selectionType !== 'pricerange' &&
                  filter.options?.map((op, oidx) => {
                    const optionName = `${filter.uid}_${op.uid}`
                    const onFilterChange = (value: boolean) => {
                      return handleChangeFilter(filter.uid, value, op.uid)
                    }

                    return (
                      <div key={oidx} className={styles.filterOption}>
                        {filter.selectionType === 'single' && (
                          <Radio
                            label={renderLabel(op.text, op.count)}
                            theme={{
                              root: 'w-full py-2',
                              content: 'order-1 flex-1 max-w-none',
                              radio: 'order-2',
                            }}
                            name={optionName}
                            defaultChecked={false}
                            checked={selectedFilterMap?.[filter.uid]?.includes(
                              op.uid
                            )}
                            onChange={onFilterChange}
                          />
                        )}

                        {filter.selectionType === 'multiple' && (
                          <Checkbox
                            label={renderLabel(op.text, op.count)}
                            name={optionName}
                            size='md'
                            defaultChecked={false}
                            checked={selectedFilterMap?.[filter.uid]?.includes(
                              op.uid
                            )}
                            theme={{
                              root: 'w-full py-2',
                              content: 'order-1 flex-1 max-w-none',
                              box: 'order-2',
                            }}
                            onChange={onFilterChange}
                          />
                        )}
                      </div>
                    )
                  })}
              </AccordionItem>
            )
          })}
        </Accordion>
      </div>
    )
  }

  const renderFooter = useCallback(() => {
    return (
      <>
        <Button
          status='primary'
          onClick={handleApplyFilters}
          fluid
          size='lg'
          theme={{
            root: 'max-md:h-11 max-md:text-sm max-md:leading-4',
          }}>
          Apply Filters
        </Button>
        <Button
          status='secondary'
          fluid
          onClick={handleClearFilters}
          size='lg'
          theme={{
            root: 'max-md:h-11 max-md:text-sm max-md:leading-4',
          }}>
          Clear Filters
        </Button>
      </>
    )
  }, [handleApplyFilters, handleClearFilters])

  return (
    <>
      <Drawer
        wrapperClass={classNames(styles.wrapper, 'hidden md:block')}
        title={drawerTitle}
        isOpen={open}
        placement='right'
        width={Number('480')}
        onClose={handleClose}>
        {renderSelected()}
        {renderContent()}
        <div
          className={classNames(
            styles.footer,
            'flex items-center gap-x-4 w-full px-6 border-t'
          )}>
          {renderFooter()}
        </div>
      </Drawer>
      <Drawer
        wrapperClass={classNames(styles.wrapperBottom, 'md:hidden')}
        title={drawerTitle}
        isOpen={open}
        placement='bottom'
        onClose={handleClose}>
        {renderSelected()}
        {renderContent()}
        <div
          className={classNames(
            styles.footerBottom,
            'flex items-center gap-x-4 w-full px-4 border-t'
          )}>
          {renderFooter()}
        </div>
      </Drawer>
    </>
  )
}

export default FiltersDrawer
